/**
 * Tasks Domain API Schemas
 *
 * Zod schemas for the AI Tasks API endpoints.
 * Covers model config update (single and bulk).
 */

import { z } from "zod";

import type { DbEntity } from "../../../client";
import { ModelConfigBaseShape } from "../../../schemas/ModelConfig/model-config.schema";
import { taskFormSchemaBase } from "../../../shared/domain/tools/aitask.types";
import { ApiSuccessSchema } from "../../common/response.schema";

// modelName requires min(1) — task updates must name a model explicitly.
// All other fields inherit from ModelConfigBaseShape.
const modelConfigBodyShape = {
  ...ModelConfigBaseShape,
  modelName: ModelConfigBaseShape.modelName.min(1)
};

const ModelConfigBody = z.object(modelConfigBodyShape);

/**
 * A PARTIAL rename/redescribe, DERIVED from the full task form rather than
 * restated — see `taskFormSchemaBase`'s own note for why a second copy of these
 * two field rules is a drift waiting to happen.
 *
 * 🚨 AT LEAST ONE FIELD IS REQUIRED, and that refusal is the schema's job
 * rather than a nicety. `.partial()` alone accepts `{}`, which would bump
 * `updatedAt` and emit an update event for a request that changed nothing — a
 * no-op that reads as a real edit in every audit surface downstream.
 */
const TaskDetailsBody = taskFormSchemaBase
  .pick({ name: true, description: true })
  .partial()
  .refine(
    (body) => body.name !== undefined || body.description !== undefined,
    "Provide at least one of name or description"
  );

// ============================================
// ZTasks — NexusApi Endpoint Definitions
// ============================================

export const ZTasks = {
  /**
   * Update model config for a single AI task.
   * Task ownership is verified by TaskOwnershipGuard before this endpoint runs.
   * POST /api/tools/ai-tasks/:taskId/update-model-config
   */
  UpdateModelConfig: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/update-model-config",
    PathVars: z.object({ taskId: z.string().uuid() }),
    Body: ModelConfigBody,
    Response: ApiSuccessSchema(z.custom<DbEntity.AITask>())
  },

  /**
   * Rename an AI task, or rewrite its description, without resubmitting it.
   *
   * 🚨 WHY THIS EXISTS BESIDE `ZAiTasks.Update`. That route is `PUT` over the
   * WHOLE `taskCreationSchema`, with `name` required — so renaming a task from
   * a menu meant reading the task, patching one field and sending everything
   * back. That read-modify-write races any concurrent edit and silently
   * discards it, and the wider the form grows the more it can lose. A caller
   * that wants to change a name must be able to send a name.
   *
   * PATCH rather than the sibling's POST-with-a-verb-path: this is a partial
   * update of the task itself, which is what PATCH means, and `/details`
   * matches `PUT /api/workflow/:id/details` — the identical concept one
   * resource type over.
   *
   * Task ownership is verified by `TaskOwnershipGuard` before this runs.
   */
  UpdateDetails: {
    method: "PATCH" as const,
    path: "/api/tools/ai-tasks/:taskId/details",
    PathVars: z.object({ taskId: z.string().uuid() }),
    Body: TaskDetailsBody,
    Response: ApiSuccessSchema(z.custom<DbEntity.AITask>())
  },

  /**
   * Bulk update model config for multiple AI tasks.
   * POST /api/tools/ai-tasks/bulk/update-model-config
   */
  BulkUpdateModelConfig: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/bulk/update-model-config",
    Body: z.object({
      taskIds: z.array(z.string().uuid()).min(1),
      ...modelConfigBodyShape
    }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.array(z.string()),
        failed: z.array(z.object({ id: z.string(), error: z.string() })),
        totalRequested: z.number(),
        totalSuccess: z.number(),
        totalFailed: z.number()
      })
    )
  }
} as const;
